import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Upload } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { GuiaExportarChat } from '@/components/contacts/GuiaExportarChat';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectorDeFuente } from '@/components/cartera/FuenteDelCliente';
import { useFuentes } from '@/hooks/useFuentes';
import { useToast } from '@/hooks/useToast';
import {
  revisarContactos,
  importarContactos,
  type Formato,
  type RevisionDeContactos,
  type ResultadoDeContactos,
} from '@/api/importacion.api';
import { aBase64, cambiarMapeo, formatoDeArchivo, fuenteDelArchivo, textoDelResumen } from '@/lib/importacion';

/**
 * IMPORTAR CONTACTOS (tanda 3, Ola D; P11). el dueño: «tengo Excel hecho con algunos
 * Leads» (audio-1).
 *
 * Dos pasos, como el inventario: se REVISA sin guardar nada (qué es cada columna,
 * cuáles son nuevos, cuáles ya están, cuáles tienen baja, qué filas están mal) y
 * recién después se importa. Importar no le escribe a nadie: cada contacto nuevo
 * trae un primer saludo que espera la aprobación del dueño en Seguimientos.
 */

/** La revisión, sin estado: se prueba con renderToStaticMarkup. */
export function RevisionView({
  revision: r,
  mapeo,
  onMapeo,
  onImportar,
  importando,
}: {
  revision: RevisionDeContactos;
  mapeo: Record<string, string>;
  onMapeo: (columna: string, destino: string) => void;
  onImportar: () => void;
  importando: boolean;
}) {
  const n = r.nuevos.length;
  return (
    <Card>
      <CardBody className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">{`Se crean en el canal «${r.canal.nombre}».`}</p>
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">¿Qué es cada columna?</p>
          {r.columnas.map((c) => (
            <label key={c} className="flex items-center justify-between gap-2 text-sm">
              <span className="truncate text-slate-700 dark:text-slate-200">{c}</span>
              <select
                value={mapeo[c] ?? ''}
                onChange={(e) => onMapeo(c, e.target.value)}
                className="min-h-[44px] rounded-lg border border-slate-200 px-2 dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="">No importar</option>
                {r.destinos.map((d) => (
                  <option key={d.clave} value={d.clave}>
                    {d.etiqueta}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
        {r.aviso && (
          <p className="flex items-start gap-2 rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {r.aviso}
          </p>
        )}
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{textoDelResumen(r.resumen)}</p>
        {n > 0 && (
          <ul className="space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
            {r.nuevos.slice(0, 10).map((f) => (
              <li key={f.telefono}>{`${f.nombre ?? 'Sin nombre'} · ${f.telefono}`}</li>
            ))}
            {n > 10 && <li>{`…y ${n - 10} más`}</li>}
          </ul>
        )}
        {r.malas.length > 0 && (
          <ul className="space-y-0.5 text-xs text-amber-800 dark:text-amber-200">
            {r.malas.slice(0, 20).map((m) => (
              <li key={m.linea}>{`Fila ${m.linea}: ${m.motivo}`}</li>
            ))}
          </ul>
        )}
        <p className="text-xs text-slate-500">
          Cada contacto nuevo trae un primer saludo que espera tu aprobación en Seguimientos: no sale nada solo. Los que ya estaban no se tocan, y a los que pidieron la baja no se les escribe.
        </p>
        <Button className="w-full" disabled={Boolean(r.aviso) || n === 0 || importando} onClick={onImportar}>
          {importando ? 'Importando…' : `Importar ${n} contacto${n === 1 ? '' : 's'}`}
        </Button>
      </CardBody>
    </Card>
  );
}

export default function ImportarContactos() {
  const toast = useToast();
  const qc = useQueryClient();
  const [archivo, setArchivo] = useState<{ contenido: string; formato: Formato; nombre: string } | null>(null);
  const [revision, setRevision] = useState<RevisionDeContactos | null>(null);
  const [mapeo, setMapeo] = useState<Record<string, string>>({});
  const [hecho, setHecho] = useState<ResultadoDeContactos | null>(null);
  // T14: de qué base es el archivo. Arranca con su nombre; todos los nuevos quedan con esta fuente.
  const [fuente, setFuente] = useState('');
  const fuentes = useFuentes();

  const revisar = useMutation({ mutationFn: revisarContactos, onError: (e: Error) => toast.error(e.message) });
  // Solo cuenta la ÚLTIMA revisión: una respuesta vieja que llega tarde no pisa la
  // pantalla ni el mapeo que el dueño dejó elegido (revisión de Codex).
  const ultimaRevision = useRef(0);
  const pedirRevision = (vars: Parameters<typeof revisarContactos>[0]) => {
    const id = ++ultimaRevision.current;
    revisar.mutate(vars, {
      onSuccess: (r) => {
        if (id !== ultimaRevision.current) return;
        setRevision(r);
        // El mapeo que propone el servidor solo se toma al leer el archivo; después manda el del dueño.
        if (!vars.mapeo) setMapeo(r.mapeo);
      },
    });
  };
  const importar = useMutation({
    mutationFn: () => importarContactos({ archivo: archivo!.contenido, formato: archivo!.formato, mapeo, fuente: fuente.trim() || undefined }),
    onSuccess: (r) => {
      setHecho(r);
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['cartera'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const leer = async (f: File) => {
    const formato = formatoDeArchivo(f.name);
    if (!formato) {
      toast.error('Sube un .xlsx o un .csv. Si es un Excel viejo (.xls), guárdalo como .xlsx.');
      return;
    }
    const contenido = formato === 'xlsx' ? aBase64(await f.arrayBuffer()) : await f.text();
    setArchivo({ contenido, formato, nombre: f.name });
    setFuente(fuenteDelArchivo(f.name));
    setRevision(null);
    setHecho(null);
    pedirRevision({ archivo: contenido, formato });
  };

  const cambiar = (columna: string, destino: string) => {
    const m = cambiarMapeo(mapeo, columna, destino);
    setMapeo(m);
    if (archivo) pedirRevision({ archivo: archivo.contenido, formato: archivo.formato, mapeo: m });
  };

  if (hecho) {
    return (
      <div className="space-y-3">
        <PageHeader title="Importar contactos" />
        <Card>
          <CardBody className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-5 w-5" /> {`${hecho.creados} contacto${hecho.creados === 1 ? '' : 's'} nuevo${hecho.creados === 1 ? '' : 's'}.`}
            </p>
            {hecho.primerosToques > 0 && (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {`${hecho.primerosToques} primer${hecho.primerosToques === 1 ? '' : 'os'} saludo${hecho.primerosToques === 1 ? '' : 's'} esperan tu aprobación en Seguimientos. No sale nada sin que lo apruebes.`}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Link to="/seguimientos" className="text-sm text-brand-700 underline dark:text-brand-300">
                Ir a Seguimientos
              </Link>
              <Button
                variant="outline"
                onClick={() => {
                  setHecho(null);
                  setArchivo(null);
                  setRevision(null);
                }}
              >
                Importar otro
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader title="Importar contactos" description="Sube tu Excel (.xlsx) o un CSV. Primero se revisa, sin guardar nada; después decides." />
      <Link to="/contacts/importar-chat" className="block text-sm text-brand-700 underline dark:text-brand-300">
        ¿Tienes chats de WhatsApp exportados (.txt)? Tráelos aquí
      </Link>
      <GuiaExportarChat />
      <label className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-6 text-center dark:border-slate-700">
        <input
          type="file"
          accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void leer(f);
          }}
        />
        <Upload className="h-6 w-6 text-slate-400" />
        <span className="text-sm text-slate-600 dark:text-slate-300">{archivo ? archivo.nombre : 'Elige tu Excel o tu CSV'}</span>
      </label>
      {archivo && (
        <Card>
          <CardBody>
            <SelectorDeFuente
              valor={fuente}
              onCambiar={setFuente}
              fuentes={fuentes}
              etiqueta="¿De qué base es este archivo?"
              ayuda="Elige una que ya tengas o escribe otra («Promoción octubre», «Base externa»…). Así en Clientes sabrás de dónde vino cada uno y cuál rinde más."
            />
          </CardBody>
        </Card>
      )}
      {revisar.isPending && <p className="text-center text-sm text-slate-500">Revisando el archivo…</p>}
      {revision && <RevisionView revision={revision} mapeo={mapeo} onMapeo={cambiar} onImportar={() => importar.mutate()} importando={importar.isPending || revisar.isPending} />}
    </div>
  );
}
